import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { ACCENT } from "./accent";
import { createComparison } from "./api/newTelemetry";

const MAX_COMPARE = 6;

/** Create a saved comparison: one base log vs. up to 6 others. */
const NewComparisonModal = ({ open, onClose, logs, onCreated }) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [baseId, setBaseId] = useState("");
  const [compareIds, setCompareIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Default the base to the first log when the modal opens.
  useEffect(() => {
    if (open && logs.length && !baseId) setBaseId(logs[0].id);
  }, [open, logs, baseId]);

  const others = useMemo(() => logs.filter((l) => l.id !== baseId), [logs, baseId]);

  const toggle = (id) => {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_COMPARE) next.add(id);
      return next;
    });
  };

  const reset = () => {
    setName("");
    setBaseId("");
    setCompareIds(new Set());
    setError(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createComparison({
        name: name.trim(),
        baseLogId: baseId,
        compareLogIds: [...compareIds].filter((id) => id !== baseId),
      });
      reset();
      onClose?.();
      onCreated?.(created.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedCompare = [...compareIds].filter((id) => id !== baseId);
  const canCreate = name.trim() && baseId && selectedCompare.length >= 1 && !busy;

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose?.();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>New comparison</DialogTitle>
      <DialogContent>
        <TextField
          label="Comparison name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1, mb: 2 }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Base log</InputLabel>
          <Select
            label="Base log"
            value={baseId}
            onChange={(e) => {
              setBaseId(e.target.value);
              setCompareIds((prev) => {
                const next = new Set(prev);
                next.delete(e.target.value);
                return next;
              });
            }}
          >
            {logs.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.file_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
          Compare against ({selectedCompare.length}/{MAX_COMPARE})
        </Typography>
        <Box sx={{ maxHeight: 220, overflow: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: 1, px: 1 }}>
          {others.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, p: 1.5 }}>
              No other ready logs to compare.
            </Typography>
          ) : (
            others.map((l) => (
              <FormControlLabel
                key={l.id}
                sx={{ display: "flex" }}
                control={
                  <Checkbox
                    checked={compareIds.has(l.id)}
                    onChange={() => toggle(l.id)}
                    disabled={!compareIds.has(l.id) && compareIds.size >= MAX_COMPARE}
                    sx={{ color: ACCENT, "&.Mui-checked": { color: ACCENT } }}
                  />
                }
                label={<Typography variant="body2">{l.file_name}</Typography>}
              />
            ))
          )}
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => {
            reset();
            onClose?.();
          }}
          sx={{ textTransform: "none", color: theme.palette.text.secondary }}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={!canCreate}
          variant="contained"
          sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#2f7ae0" } }}
        >
          {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewComparisonModal;
